// Fights between companions. Now and then two on screen that fight have a duel, one against one;
// rarely, one that can turn evil does, and goes after the others.
//
// A duel: both notice each other and get angry, then go at it at the same time. Each runs its own
// fight (fighter), deciding again and again from where things stand (choose): how far apart they
// are, whether it fights up close or from afar (the "range" of its attack ability), whether the
// other is coming at it or winding up an attack, its own cooldown, and a bit of temper and chance.
// One that fights up close runs at the other (or dashes, or takes its own way or its world's: a
// hook, a pearl, a portal, flying, teleporting, digging...), leaps over it to get behind it, and
// strikes with its own attack, the one it uses on pests (abilities/). One that shoots keeps its
// distance, backs off when the other comes close and gets away (a blink, a pearl, a hook, a
// wormhole...) when it's caught. Both dodge what they see coming: a hop back, a duck under a shot,
// a drift aside for a flier, a blink for a teleporter, a leap over an attacker up close.
//
// Hits are earned. An attack winds up visibly first, and its blow only lands if the other is
// still in reach (up close) or where the shot arrives (from afar it's aimed where the other was
// heading), and not mid-dodge; else it's a miss, a whoosh of air. A hit flashes the other red with
// a spark and staggers it: its own fight sees that, drops what it was doing and is knocked back.
// An attack that takes hold of the other (throws it, reels it in, swallows or mails it) has its
// body until it lets go. Each takes 3 to 5 hits (counted, not shown); with none left it tips over
// and goes up in a puff, and a little later it's back somewhere else on screen. The winner
// celebrates, and the others on screen watch and cheer (watch). A duel that drags on is decided on
// the hits each has left.
//
// The attacks were written for pests, so in a duel they're aimed at a stand-in for the other
// companion (standIn), a target that behaves like a pest. An attack made of several moves (its
// ability's "moves", like Ares's) has its special and big moves picked much more often than its
// plain one (pickMove), and a move that reaches far is used from afar too.
//
// A design can have fight stats ("fight" in design.json): how many hits it takes ("hits": [lo,
// hi], else 3 to 5), how quick its attacks are in a duel ("tempo": 0.6 takes 0.6 of the time) and
// its "ultra": an ability it unleashes once when it's losing, which beats the other at once, and
// now and then everyone watching too (unleash). Ares has them all; the other gods a quicker tempo.
//
// Evil: its eyes glow red (or as its design says: Steve's turn white, Herobrine) and it goes after
// the others one by one (hunt): those that fight have a duel with it, the rest run. It's over when
// it's beaten, or after a while it calms down.
//
// Everything runs on the companions' run tokens (core.js): a duel that's interrupted (a fighter
// picked up or clicked, taken off screen, its window closed) stops at once, and whoever is left
// goes back to its own life. Inside a duel, each move runs on a token of its own that a hit or a
// dodge can cut short (perform).

import { clockNow } from './clock.js';
import { env, clamp, rand, chance, pick, lerp, weighted, easeOut, tick, wait, animate, frame, isCurrent, Cancelled } from './core.js';
import { cast, setShape, pulse, spotPoint, snapShape, resetPose } from './companion.js';
import { travelTo, arrive, fallDown, baseOf } from './travel.js';
import { discoverLedges, visibleOn, surfaceBelow, FLOOR, castleLeft } from './world.js';
import { clearProps } from './stage.js';
import { BANG } from './props.js';
import { sprite } from './pixels.js';
import { blendHex } from './color.js';
import * as fx from './particles.js';
import { can, attackStyle, walkStyle, ownTravel } from './catalog.js';
import { ability } from './abilities.js';
import { attackWith, chooseSpot, standHere, happyHop, lookAround, wave } from './acts.js';

// ---------- the look of it ----------
// The anger mark that pops up when it picks a fight, and a question mark when its fight is called off.
const ANGER = sprite(`
.R.R.
RR.RR
.....
RR.RR
.R.R.
`, { R: '#E8343F' }, -2, -2);
const HUH = sprite(`
.QQ.
...Q
..Q.
....
..Q.
`, { Q: '#FFE38A' }, -2, -2);
// Hit, a companion flashes red (as mobs do in Minecraft), and sparks fly where it's hit.
const HURT = c => (c[0] === '#' ? blendHex(c.slice(0, 7), '#FF1E1E', 0.55) : '#FF5A5A');
const SPARK = ['#FFFFFF', '#FFF3A0', '#FFD84A'];
const AIR = ['#FFFFFF', '#DCE3EA'];
const POOF = ['#FFFFFF', '#E6E6E6', '#C8C8C8'];
const GOLD = ['#FFE680', '#FFC93C', '#FFF6CF', '#FFFFFF'];
const DUST = ['#E9E2D8', '#D8CFC2'];
const EVIL_SMOKE = ['#5A1020', '#8E1B2C', '#2B0A14'];

// ---------- how it fights ----------
const STRIKE_GAP = 3;          // up close: how far apart (pixels at screen size) it gets to attack
const REACH = 11;              // ... and how far apart a blow still lands
const FAR = [26, 64];          // shooting: how far it likes to be from the other (pixels at screen size)
const SHOT = 160;              // ... and how far it can shoot
const LONGEST = 20000;         // ms of fighting before it's decided on hits left

// From how far it fights: "far" for an attack that flies or reaches across ("range": "far" in the
// ability: arrows, fireballs, beams, skulls, a tongue...), else up close.
export const rangeOf = me => (ability(attackStyle(me.design))?.range === 'far' ? 'far' : 'near');

const half = me => (me.shape.bw / 2) * me.s;
const height = me => (me.shape.bh + me.shape.leg) * me.s;
const baseUnder = me => (me.anchor ? baseOf(me.anchor) : null);
const gapBetween = (a, b) => Math.abs(a.x - b.x) - half(a) - half(b);
const apart = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const level = (a, b) => Math.abs(a.y - b.y) <= 4 * env.S;
const inReach = (a, b, gap = REACH) => level(a, b) && gapBetween(a, b) <= gap * env.S;
const lookAt = (me, o) => { me.pose.eyes = o.x < me.x - 2 ? 'left' : o.x > me.x + 2 ? 'right' : 'center'; };
const randInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const floats = me => ['fly', 'none'].includes(walkStyle(me.design));
// Light enough on its feet to leap over the other (not a flier, nor a heavy one).
const canLeap = me => !floats(me) && (me.design.moves?.pace ?? 1) >= 1;
// What it stands on, or where it's headed (the surface below it while it's in the air).
const groundOf = me => baseUnder(me) || (me.dest ? baseOf(me.dest) : surfaceBelow(me.x, me.y - 1).ledge);
// Where it is, or where it's going while it travels.
const goal = me => (me.moving && me.dest && spotPoint(me, me.dest)) || me;
// The moves of an attack made of several ("moves" in its ability: Ares's), or null, and how far
// apart the furthest-reaching of them still lands (0 when none reaches further than up close).
const movesOf = me => ability(attackStyle(me.design))?.moves || null;
const longReach = me => Math.max(0, ...(movesOf(me) || []).map(m => m.reach || 0));

// Something popping up over its head for a moment (the anger mark, "!", "?").
function mark(me, art) {
  const top = me.bbox ? me.bbox.t : me.y - height(me);
  fx.emit({ kind: 'sprite', art, x: me.x, y: top - 4 * me.s, vy: -3 * me.s, life: 800, scale: Math.max(1, Math.round(me.s * 0.8)) });
}

// Waits for all of them (anything on a duel's token stops at its next frame once the duel is off),
// then passes on the first error.
async function both(...runs) {
  const out = await Promise.allSettled(runs.filter(Boolean));
  const bad = out.find(r => r.status === 'rejected');
  if (bad) throw bad.reason;
}

// Where it can stand on a surface: not over its ends (the floor ends at the sandcastle) and, in
// the gallery, inside the picture.
function span(me, base, D) {
  const g = base?.geom();
  if (!g) return null;
  const room = (me.shape.bw / 2 + 1) * me.s;
  let lo = g.x1 + room, hi = (base === FLOOR ? castleLeft() : g.x2) - room;
  if (D?.area) { lo = Math.max(lo, D.area.x1 + room); hi = Math.min(hi, D.area.x2 - room); }
  return hi >= lo ? { lo, hi, g } : null;
}

// ---------- the stand-in ----------
// What an attack is aimed at in a duel. It behaves like a pest (pests.js): x and y (its feet), s,
// ledge and dx (its surface), face, alpha, hit, alive, type and colors. While it's "on" the other
// companion it's where that one is (x at the side facing the attacker, y where a pest's middle
// would be the companion's middle); a shot from afar is aimed once, where the shooter expects the
// other to be by the time it lands (leadOf). The attack's blows are judged as they happen (there):
// a blow (hit = 1, or the finishing one: killMite, or alive = false when it's taken away:
// swallowed, mailed, shipped) that lands flashes the other and staggers it (landed), and the first
// one that lands costs it a hit. A blow that misses leaves the stand-in where the blow went, and
// the rest of the attack goes there, at nothing. An attack that moves the stand-in (x, y, s,
// alpha, ledge = null: thrown about, reeled in, swallowed) takes hold of the other if it's there,
// and has its body until it's done (inGrip). It's never on a surface (ledge is null), so miteTo
// does nothing: the other doesn't come when an attack waits for it, and its own stagger knocks it
// back.
function standIn(D, A, B) {
  const fB = fighterOf(D, B), ranged = rangeOf(A) === 'far', dir = () => Math.sign(B.x - A.x) || 1;
  const inset = () => Math.max(0, B.shape.bw / 2 - 2) * B.s, lift = () => Math.max(0, (B.shape.bh + B.shape.leg) / 2 - 2) * B.s;
  const onIt = () => ({ x: B.x - dir() * inset(), y: B.y - B.jump + B.bob - lift() });
  const now = clockNow(), colors = ['@main', '@~main', '@+main'].map(t => B.look.color(t, 0, 0, now));
  let mode = 'on', at = null, side = 1, alive = true, hitV = 0, aim = ranged ? leadOf(D, A, B, onIt()) : null, lastBlow = -Infinity;
  // Is the other where the blow lands? Visible, not mid-dodge, and still in reach (up close: a move
  // that reaches further has its own, S.reach: strike) or where the shot was aimed (from afar).
  // Nothing lands while an ultra goes off.
  const there = () => D.live() && !D.ultra && B.alpha >= 0.5 && !fB.dodging && (ranged ? covers(B, aim) : inReach(A, B, S.reach ?? REACH) && B.jump < 4 * B.s);
  // Where a blow that misses goes: where the shot was aimed, where the other just was (a move that
  // reaches further), or the air just in front of it.
  const missAt = () => aim || (S.reach ? onIt() : { x: A.x + dir() * (half(A) + 4 * env.S), y: A.y - lift() });
  const freeze = (p = missAt()) => { if (mode === 'on') { mode = 'off'; at = { ...p, s: B.s, alpha: 1 }; } };
  const place = () => { B.x = at.x + side * inset(); B.y = at.y + lift(); };
  const grab = () => {
    mode = 'held'; side = dir(); at = { ...onIt(), s: B.s, alpha: B.alpha };
    Object.assign(S, { foeSide: side, heldAt: clockNow() });
    hold(D, B, S);
  };
  const take = () => {
    if (mode === 'on' && there()) grab();
    else freeze();
    return mode === 'held';
  };
  const blow = final => {
    const t = clockNow(), fresh = t - lastBlow > 200;     // (a flurry of blows sparks less)
    S.firstAt ??= t;
    lastBlow = t;
    if (mode === 'on' && there()) { landed(D, A, B, S, fresh); if (aim) aim = onIt(); }   // a shot that lands stays on it
    else if (mode === 'held') landed(D, A, B, S, fresh);
    else { freeze(); if (fresh) whoosh(at.x, at.y, B.s, dir()); return; }
    // Finished off without a blow of its own (no killMite): it's been taken away for a moment.
    if (final) queueMicrotask(() => { if (S.struck) return; if (mode === 'on') grab(); B.alpha = 0; fx.puff(B.x, B.y - height(B) / 2, B.s); });
  };
  const S = {
    kind: 'standIn', foe: B, from: A, type: 'companion', colors, home: B.anchor ? { ...B.anchor } : null, t0: now,
    get mode() { return mode; },
    freeze,
    get x() { return at ? at.x : (aim || onIt()).x; },
    set x(v) { if (take()) { at.x = v; place(); } else at.x = v; },
    get y() { return at ? at.y : (aim || onIt()).y; },
    set y(v) { if (take()) { at.y = v; place(); } else at.y = v; },
    get s() { return at ? at.s : B.s; },
    set s(v) { if (take()) { at.s = v; B.s = Math.max(1, Math.round(v)); place(); } else at.s = v; },
    get alpha() { return at ? at.alpha : 1; },
    set alpha(v) { if (take()) { at.alpha = v; B.alpha = clamp(v, 0, 1); } else at.alpha = v; },
    get face() { return -dir(); },
    set face(v) {},
    get ledge() { return null; },
    set ledge(v) { if (v === null && take()) S.air = true; },
    dx: 0,
    get hit() { return hitV; },
    set hit(v) { if (v && !hitV) blow(false); hitV = v; },
    get alive() { return alive; },
    set alive(v) { if (v || !alive) return; alive = false; blow(true); },
    die() { S.struck = true; },
  };
  return S;
}

// Where a shot at B goes: where it is now, plus the way it's moving for about as long as the
// shooter's shots take to land (it learns that as the duel goes on), give or take.
function leadOf(D, A, B, p) {
  const f = fighterOf(D, B), t = ((fighterOf(D, A).timing ?? 900) / 1000) * rand(0.6, 1.1);
  return { x: p.x + f.vx * t, y: p.y + f.vy * t };
}

// Is point p on B's body (give or take a little)?
const covers = (B, p) => Math.abs(p.x - B.x) <= half(B) + 3 * env.S
  && p.y >= B.y - B.jump - height(B) - 3 * env.S && p.y <= B.y - B.jump + 3 * env.S;

// A blow that lands: B flashes red and sparks fly where it's hit; the attack's first blow that
// lands costs it one of its hits, or S.power of them (a big move of Ares's: two), with more sparks;
// one with its ultra still to come makes its last stand on its last hit. Unless it's in the middle
// of an attack of its own (then they trade blows; but on its last stand it turns to its ultra) or
// the attack has hold of it, it staggers: its own fight drops what it was doing (cut) and is
// knocked back (hurt).
function landed(D, A, B, S, fresh) {
  const f = fighterOf(D, B), t0 = clockNow(), first = !S.connected;
  B.tint = c => (clockNow() - t0 < 260 ? HURT(c) : c);
  if (first || fresh) {
    fx.burst(S.x, S.y, B.s, SPARK, first ? 12 : 7, first ? 13 : 9, 0, first ? 360 : 240);
    if (first) fx.star(S.x, S.y - 2 * B.s, 420);
    D.hits++;
  }
  const last = !!f.ultra && !D.ultra;
  if (first) { S.connected = true; if (D.fighters.every(me => fighterOf(D, me).hp > 0)) f.hp = Math.max(last ? 1 : 0, f.hp - (S.power ?? 1)); }   // (not once one is beaten)
  if (!f.heldBy && (!f.attack || (last && f.hp <= 1))) { f.hurt = { from: A, hard: first }; cut(D, B); }
}

// A blow into thin air: a few streaks of it.
function whoosh(x, y, s, dir) {
  for (let i = 0; i < 4; i++) fx.emit({ kind: 'square', x: x - dir * i * 2 * s, y: y + (i - 1.5) * 1.5 * s, vx: dir * rand(16, 24) * s, life: 220, size: 0.5, color: AIR[i % 2], fade: 0.8 });
}

// The attack has hold of it: its own fight lets go of its body until the attack is done.
function hold(D, me, S) {
  const f = fighterOf(D, me);
  f.heldBy = S; f.hurt = null;
  Object.assign(me, { moving: true, dest: null, jump: 0, bob: 0 });
  cut(D, me);
}

// ---------- a duel ----------
let current = null;
// The duel going on, for the ones watching (null when there's none).
export const duelOn = () => (current?.live() ? current : null);

// A run token that stops when the duel does: what a fighter does in it ends at its next frame, as
// if cancelled, while its own run goes on (to the end of the duel). `duel` tells what it starts
// (no endermite fight out of an ender pearl: acts.js).
const inDuel = (g, D) => ({ me: g.me, duel: true, get n() { return D.live() ? g.n : -1; } });

// Both fighters, each with its own side of things: its hits left (hp, of max), the move it's doing
// (act) and its attack (attack: the stand-in it's aimed at), what's happened to it (hurt, heldBy,
// a dodge it decided on, struck by an ultra), when it may attack (next) or run off (runAt) again,
// how it's moving (vx, vy, for the other's aim), its fight stats (tempo, ultra) and its temper:
// how fast it reacts, how often it dodges, how eager it is to attack (one with several moves
// presses on). The duel also knows who's watching (watchers) and the ultra going off (ultra).
function newDuel(gA, gB, { hits = null, area = null, ledges = null }) {
  const D = { fighters: [gA.me, gB.me], hits: 0, area, ledges: ledges || discoverLedges, raw: new Map([[gA.me, gA], [gB.me, gB]]),
    over: false, stop: false, result: null, start: clockNow(), endAt: Infinity, watchers: new Set(), ultra: null };
  D.live = () => !D.over && isCurrent(gA) && isCurrent(gB);
  D.f = new Map(D.fighters.map((me, i) => {
    const stats = me.design.fight || {}, hp = hits?.[i] ?? randInt(...(stats.hits || [3, 5]));
    const quick = ownTravel(me.design).includes('teleport') ? 1.5 : floats(me) ? 1.3 : walkStyle(me.design) === 'hop' ? 1.2 : canLeap(me) ? 1 : 0.6;
    return [me, { me, g: inDuel(D.raw.get(me), D), hp, max: hp, act: null, attack: null, hurt: null, heldBy: null,
      back: null, dodge: null, seen: null, dodging: false, struck: false, next: 0, runAt: 0, timing: null, vx: 0, vy: 0, t: null,
      tempo: stats.tempo ?? 1, ultra: ability(stats.ultra)?.ultra ? stats.ultra : null,
      reflex: rand(150, 420), dodgy: Math.min(0.6, rand(0.2, 0.35) * quick), bold: movesOf(me) ? 0.95 : rand(0.65, 0.95) }];
  }));
  return D;
}
const fighterOf = (D, me) => D.f.get(me);
const foeOf = (D, me) => (D.fighters[0] === me ? D.fighters[1] : D.fighters[0]);
// (Out of time, it goes on while one with its ultra still to come is losing: it gets to unleash it.)
const fighting = D => D.live() && !D.stop && D.fighters.every(me => fighterOf(D, me).hp > 0) && (clockNow() < D.endAt || D.fighters.some(me => losing(D, me)));
// Later in a long duel they tire and dodge less.
const worn = D => clamp((clockNow() - D.start - 8000) / 12000, 0, 1);

// The duel itself, with both fighters' run tokens (runGroup in runtime/index.js). Options for the
// gallery: hits ([a, b]: how many each can take), area ({ x1, x2 }: stay inside the picture),
// ledges (what to stand on).
export async function duel(gA, gB, opts = {}) {
  const D = newDuel(gA, gB, opts);
  current = D;
  try {
    await faceOff(D);
    Object.assign(D, { start: clockNow(), endAt: clockNow() + LONGEST });
    for (const f of D.f.values()) f.next = clockNow() + rand(0, 900);   // not both at once
    await both(...D.fighters.map(me => fighter(D, me)));
  } catch (e) {
    if (!(e instanceof Cancelled)) { D.over = true; D.result = { off: true }; throw e; }
  } finally {
    if (current === D) current = null;
  }
  await finish(D);
}

// Both get up (onto a proper surface), notice each other with an angry mark and square up.
async function faceOff(D) {
  await both(...D.fighters.map(async me => {
    const g = fighterOf(D, me).g, foe = foeOf(D, me);
    standHere(me);
    const base = baseUnder(me);
    if (!base || (base.kind !== 'edge' && base.kind !== 'floor')) await goTo(g, chooseSpot(me, { only: 'floor' }) || { ledge: FLOOR, dx: clamp(me.x, 20 * env.S, castleLeft() - 20 * env.S), rest: 'stand' });
    setShape(me, 'normal', 'front');
    lookAt(me, foe);
    await wait(rand(100, 350), g);
    mark(me, ANGER);
    pulse(me, 'stretch');
    await stance(g, foe, rand(500, 800));
  }));
}

// One fighter's side of the duel, until it's decided: again and again it picks a move from where
// things stand and does it, while the other does the same.
async function fighter(D, me) {
  const f = fighterOf(D, me);
  try {
    while (fighting(D)) {
      if (f.heldBy) await inGrip(D, me);
      else await perform(D, me, ...choose(D, me));
    }
    if (f.struck && me.alpha >= 0.5) await topple(f.g, D, D.ultra.by);   // (the end takes it from there)
  } finally { me.hurry = false; me.tint = null; }
}

// What to do next: [move, keep (not cut short to dodge), stop (cut it short when this comes true)].
function choose(D, me) {
  const f = fighterOf(D, me), foe = foeOf(D, me), ready = () => clockNow() >= f.next;
  if (f.back) { const S = f.back; f.back = null; return [g => comeBack(D, g, S), true]; }
  if (!me.anchor) return [g => fallDown(g), true];                        // cut short up in the air: down first
  if (losing(D, me)) { f.hurt = null; return [g => unleash(D, g), true]; }
  if (f.hurt) { const h = f.hurt; f.hurt = null; return [g => stagger(D, g, h), true]; }
  if (D.ultra) return [g => stance(g, D.ultra.by, 200)];                  // the other's ultra going off: it stands in awe
  if (f.dodge) { const S = f.dodge; f.dodge = null; if (!S.done) return [g => dodge(D, g, S), true]; }
  if (f.calm || foe.alpha < 0.5 || fighterOf(D, foe).heldBy) return [g => stance(g, foe, 250)];   // the other is gone for a moment, or in its grip
  if (rangeOf(me) === 'near') {
    if (inReach(me, foe, STRIKE_GAP + 3)) return ready() && chance(f.bold) ? [g => strike(D, g), true] : [g => spar(D, g)];
    // A move that reaches far (a spear throw, fire from above), now and then from where it is.
    if (ready() && level(me, foe) && gapBetween(me, foe) <= longReach(me) * 0.8 * env.S && chance(0.5)) return [g => strike(D, g), true];
    // Over to it, striking as soon as it's there; on the same surface it turns when the other moves off.
    const same = level(me, foe), x0 = foe.x;
    return [g => closeIn(D, g), !same, same ? () => (ready() && inReach(me, foe, STRIKE_GAP + 3)) || Math.abs(foe.x - x0) > 24 * env.S : null];
  }
  // From afar: away when the other is right on it (mostly) or close (now and then), but not again
  // right after it got away (then it stands and fights for a moment); else it shoots, and moves
  // somewhere else when it's too far to shoot (or now and then), not while the other is on its way.
  const d = apart(me, foe);
  if (clockNow() > f.runAt && chance(inReach(foe, me) ? 0.7 : d < FAR[0] * env.S ? 0.4 : 0)) {
    return [async g => { await getAway(D, g); f.runAt = clockNow() + rand(1500, 3000); }, true];
  }
  if (ready() && canShoot(me, foe)) return [g => strike(D, g), true];
  if (!foe.moving && (d > SHOT * env.S || chance(0.12))) return [g => reposition(D, g), true];
  return [g => spar(D, g)];
}

// Does one move on a token of its own, watching every frame for what should cut it short: a hit
// (landed), an attack it decides to dodge (noticed), its moment to strike (stop) or the time
// running out. A move cut short stops at its next frame, and what it left behind is tidied up.
async function perform(D, me, move, keep = false, stop = null) {
  const f = fighterOf(D, me), act = { me, duel: true, cut: false, get n() { return act.cut ? -1 : f.g.n; } };
  let done = false;
  f.act = act;
  const run = move(act).catch(e => { if (!(e instanceof Cancelled)) throw e; }).finally(() => {
    done = true;
    if (f.act === act) f.act = null;
    if (act.cut && D.live()) tidy(D, me);
  });
  while (!done) {
    await new Promise(r => frame(r));
    track(f);
    if (done || act.cut || !D.live()) continue;
    if ((!keep && (noticed(D, me) || clockNow() > D.endAt)) || stop?.()) act.cut = true;
  }
  await run;
}

const cut = (D, me) => { const act = fighterOf(D, me).act; if (act) act.cut = true; };

// How it's moving, for the other's aim (leadOf).
function track(f) {
  const me = f.me, now = clockNow(), dt = (now - (f.t ?? now)) / 1000, max = 40 * env.S;
  if (dt > 0 && dt < 0.25) {
    f.vx = lerp(f.vx, clamp((me.x - f.px) / dt, -max, max), 0.3);
    f.vy = lerp(f.vy, clamp((me.y - f.py) / dt, -max, max), 0.3);
  }
  Object.assign(f, { px: me.x, py: me.y, t: now });
}

// The other's attack, once it's seen it coming (after its reflexes): now and then it decides to
// get out of the way (the quicker it is, the more often; less as the duel wears on).
function noticed(D, me) {
  const f = fighterOf(D, me), S = fighterOf(D, foeOf(D, me)).attack;
  if (!S || S === f.seen || clockNow() - S.t0 < f.reflex) return false;
  f.seen = S;
  if (!chance(f.dodgy * (1 - 0.5 * worn(D)))) return false;
  f.dodge = S;
  return true;
}

// After a move was cut short: whatever it had out goes away and it stands normally again, on what
// it was moving along (up in the air, it falls next: choose). In the other's grip it stays where
// that has it.
function tidy(D, me) {
  clearProps(me);
  resetPose(me);
  Object.assign(me, { jump: 0, bob: 0, chute: 0, sink: 0, hurry: false, dest: null });
  setShape(me, 'normal', 'front');
  if (fighterOf(D, me).heldBy) { me.moving = true; return; }
  me.alpha = 1;
  if (!standsOn(me, D)) { me.anchor = null; me.moving = true; }
}

// If it's on a surface (not up in the air), it stands on it where it is: true.
function standsOn(me, D) {
  const below = surfaceBelow(me.x, me.y - 1);
  if (Math.abs(below.g.y - me.y) >= 2 * me.s) return false;
  landHere(me, D);
  return true;
}

// ---------- the moves ----------
// An attack, at its own tempo: up close it first steps right up to the other (unless it's using a
// move that reaches from where it is); then a short wind-up the other can see coming (a crouch and
// a glint) and its own attack (abilities/, or the move picked of several), aimed at a stand-in for
// the other. Then a moment before it can attack again.
async function strike(D, g) {
  const me = g.me, f = fighterOf(D, me), foe = foeOf(D, me), toward = Math.sign(foe.x - me.x) || 1, gap = gapBetween(me, foe);
  g.tempo = f.tempo;
  if (rangeOf(me) === 'near' && gap > (STRIKE_GAP + 1) * env.S && gap <= REACH * env.S) await nudge(g, toward * (gap - STRIKE_GAP * env.S), 1, 200, D);
  const m = pickMove(D, me, foe), S = standIn(D, me, foe);
  if (m) Object.assign(S, { reach: m.reach, power: m.power });
  f.attack = S;
  try {
    lookAt(me, foe);
    pulse(me, 'squash');
    fx.star(me.x + toward * half(me), me.y - height(me) * 0.6, 300);
    await wait(rand(120, 220), g);
    await (m ? m.move.attack(g, S) : attackWith(attackStyle(me.design))(g, S));
  } finally {
    S.done = true;
    f.attack = null;
    if (S.firstAt) f.timing = S.firstAt - S.t0;
    f.next = clockNow() + rand(150, 600) * f.tempo;
  }
}

// The move of an attack made of several (its "moves"), or null: its special and big ones much
// more often than its plain one (each move's "duel" weight), never the same twice running, and
// from afar only one that reaches that far ("reach"; all of a ranged attack's do).
function pickMove(D, me, foe) {
  const f = fighterOf(D, me), gap = gapBetween(me, foe) / env.S;
  const ok = (movesOf(me) || []).filter(m => rangeOf(me) === 'far' || gap <= (m.reach ?? REACH));
  const m = weighted(ok, m => (m.id === f.lastMove && ok.length > 1 ? 0 : m.duel ?? 1));
  if (m) f.lastMove = m.id;
  return m;
}

// ---------- the ultra ----------
// Losing, with an ultra it hasn't used: down to its last hit (where it makes its last stand:
// landed), clearly behind, or not ahead as the time runs out. (One ultra a duel.)
function losing(D, me) {
  const f = fighterOf(D, me), o = fighterOf(D, foeOf(D, me));
  return !!f.ultra && !D.ultra && (f.hp <= 1 || f.hp / f.max + 0.35 < o.hp / o.max || (clockNow() > D.endAt - 4000 && f.hp <= o.hp));
}

// Its ultra (an ability with ultra(g, targets, strike)): the other stands in awe, nothing else
// lands, and it's aimed at the other, or now and then (1 in 3) at everyone watching too. The
// ability calls strike(target) as its blast hits each one: a flash, and it topples (and comes back
// a little later: beaten); whoever it didn't reach is struck at its end. The other is beaten.
async function unleash(D, g) {
  const me = g.me, foe = foeOf(D, me), all = chance(1 / 3) ? [...D.watchers].filter(o => o.alpha >= 0.5) : [];
  const U = D.ultra = { by: me, targets: [foe, ...all], struck: new Set() };
  cut(D, foe);
  const strike = target => {
    if (!U.targets.includes(target) || U.struck.has(target) || !D.live()) return;
    U.struck.add(target);
    const t0 = clockNow();
    target.tint = c => (clockNow() - t0 < 400 ? HURT(c) : c);
    fx.burst(target.x, target.y - height(target) / 2, target.s, SPARK, 16, 14, 0, 420);
    if (target === foe) { Object.assign(fighterOf(D, foe), { hp: 0, struck: true }); cut(D, foe); }
  };
  try { await ability(fighterOf(D, me).ultra).ultra(g, U.targets, strike); } finally { U.targets.forEach(strike); }
}

// Up close: over to the other, beside it (the side it comes from, or now and then round the
// back): a dash when it's close, else running along its surface (its fast ways when it's far), or
// its own way to another surface.
async function closeIn(D, g) {
  const me = g.me, foe = foeOf(D, me), to = goal(foe), base = groundOf(foe), sp = span(me, base, D);
  if (!sp) return stance(g, foe, 300);
  const same = baseUnder(me)?.id === base.id, from = Math.sign(me.x - to.x) || 1, side = same || chance(0.75) ? from : -from;
  const want = half(foe) + half(me) + STRIKE_GAP * env.S;
  const x = [side, -side].map(k => to.x + k * want).find(c => c >= sp.lo && c <= sp.hi) ?? clamp(to.x + side * want, sp.lo, sp.hi);
  if (same && Math.abs(x - me.x) < 30 * env.S && !floats(me) && chance(0.4)) return nudge(g, x - me.x, 3, 300, D);
  await goTo(g, { ledge: base, dx: x - sp.g.x1, rest: 'stand' });
}

// From afar, the other too close: back off along its surface (a run or a big hop back) while
// there's room and it isn't caught; else off to somewhere else in range, its fastest way (a blink,
// a pearl, a hook, a wormhole, flying...); cornered with nowhere to go, over the other's head.
async function getAway(D, g) {
  const me = g.me, foe = foeOf(D, me), away = Math.sign(me.x - foe.x) || 1;
  const back = !inReach(foe, me) && chance(0.65) && freeAlong(me, away, 16, 30, D);
  if (back) return chance(0.5) && !floats(me) ? nudge(g, spotPoint(me, back).x - me.x, 5, 420, D) : goTo(g, back);
  const spot = spotAround(me, foe, D, ...FAR.map(v => v * env.S), true);
  if (spot) return travelTo(spot, g, fastWay(me, spot));
  if (canLeap(me) && inReach(foe, me)) return leapOver(D, g);
  return stance(g, foe, 300);
}

// From afar: to somewhere else in range (nearer when it's too far), rather on its own surface.
async function reposition(D, g) {
  const me = g.me, foe = foeOf(D, me), spot = spotAround(me, foe, D, ...FAR.map(v => v * env.S), false);
  await (spot ? goTo(g, spot) : stance(g, foe, 300));
}

// While it can't attack yet: on its toes facing the other, a quick hop back, now and then a leap
// right over the other (up close) or a taunt.
async function spar(D, g) {
  const me = g.me, foe = foeOf(D, me), r = Math.random(), back = Math.sign(me.x - foe.x) || 1;
  if (r < 0.25 && rangeOf(me) === 'near' && inReach(me, foe) && canLeap(me)) return leapOver(D, g);
  if (r < 0.55) return nudge(g, back * rand(3, 8) * me.s, 2, 220, D);
  if (r < 0.62) return taunt(g, foe);
  return stance(g, foe, rand(200, 450));
}

// A leap right over the other with a somersault, landing behind it (if there's room there).
async function leapOver(D, g) {
  const me = g.me, foe = foeOf(D, me), side = Math.sign(foe.x - me.x) || 1, sp = span(me, baseUnder(me), D);
  const x = foe.x + side * (half(foe) + half(me) + STRIKE_GAP * env.S);
  if (!sp || x < sp.lo || x > sp.hi || !level(me, foe)) return stance(g, foe, 250);
  await nudge(g, x - me.x, (height(foe) / me.s) * 1.3 + 4, 460, D, side);
  lookAt(me, foe);
}

// Out of the way of an attack it sees coming, its own way: a teleporter blinks aside, under a shot
// it may duck, up close it may leap right over the attacker (and come straight back at it), else
// it hops back (a flier drifts). Nothing hits it while it dodges.
async function dodge(D, g, S) {
  const me = g.me, f = fighterOf(D, me), away = Math.sign(me.x - S.from.x) || 1, room = roomToward(me, away, D);
  const ranged = rangeOf(S.from) === 'far', blink = ownTravel(me.design).find(w => w === 'teleport' && can(me, w) && ability(w)?.travel);
  f.dodging = true;
  try {
    if (blink && room > 12 * me.s) await travelTo(along(me, away * Math.min(room, rand(16, 26) * me.s)), g, blink);
    else if (ranged && !floats(me) && (room < 7 * me.s || chance(0.4))) await duck(g, S);
    else if (!ranged && canLeap(me) && chance(0.35)) { await leapOver(D, g); f.next = 0; }
    else await nudge(g, away * Math.min(room, rand(9, 14) * me.s), floats(me) ? 2 : 4, floats(me) ? 340 : 280, D);
  } finally { f.dodging = false; }
}

// Down low, its arms over its head, while the shot goes over; or at once into a look of its own for
// ducking, at that look's size ("duck" in its variants: the Chrome dino ducks as in its game).
async function duck(g, S) {
  const me = g.me, p = me.pose, sh = me.shapes.normal, t0 = clockNow(), low = me.design.body.variants?.duck?.art;
  S.freeze({ x: S.x, y: me.y - height(me) * 1.05 });
  if (low) {
    me.shape = { bw: low[0].length, bh: low.length, leg: 0 };
    me.target = { ...me.shape, view: me.view, face: me.face };
    Object.assign(p, { variant: 'duck', armL: 'none', armR: 'none' });
  } else {
    me.target = { ...sh, bh: Math.max(2, sh.bh - Math.ceil(sh.bh / 3)), leg: Math.max(0, sh.leg - 1), view: 'front', face: me.face };
    Object.assign(p, { eyes: 'closed', armL: 'up', armR: 'up' });
  }
  try {
    while (!S.done && clockNow() - t0 < 1100) await tick(g);
    await wait(100, g);
  } finally { if (low) { p.variant = null; me.shape = { ...sh }; me.target = { ...sh, view: me.view, face: me.face }; } }   // (back up at once, even cut short)
  Object.assign(p, { eyes: 'center', armL: 'side', armR: 'side' });
  setShape(me, 'normal', 'front');
}

// Hit: eyes shut, a squash, knocked back a little by the blow that cost it a hit (the attack's
// next blows find it still in reach), then a wide-eyed moment.
async function stagger(D, g, { from, hard }) {
  const me = g.me;
  me.pose.eyes = 'closed';
  pulse(me, 'squash');
  if (hard) await nudge(g, (Math.sign(me.x - from.x) || 1) * 4 * me.s, 2, 240, D);
  else await wait(200, g);
  me.pose.eyes = 'wide';
  await wait(hard ? 300 : 200, g);
  lookAt(me, from);
}

// In the other's grip (thrown about, reeled in, swallowed, mailed away): arms up, legs kicking,
// eyes wide, tumbling when it's thrown, until the attack lets go of it; then it gets back on its
// feet (comeBack, its next move). An attack that holds it still (`still` on the stand-in: turned to
// stone) leaves it as it was.
async function inGrip(D, me) {
  const f = fighterOf(D, me), S = f.heldBy;
  try {
    while (!S.done) {
      await tick(f.g);
      me.moving = true;
      if (me.alpha < 0.5 || S.still) continue;
      Object.assign(me.pose, { armL: 'up', armR: 'up', legs: 'kick', eyes: 'wide' });
      if (S.air) { me.rotAround = 'center'; me.rot = S.foeSide * ((clockNow() - S.heldAt) / 1000) * Math.PI * 2 * 0.8; }
    }
  } finally { f.heldBy = null; f.hurt = null; }
  f.back = S;
}

// Let go of: back on its feet at its own size, down to the surface under it, or (taken away)
// back where it was with a puff after a moment, a little dizzy.
async function comeBack(D, g, S) {
  const me = g.me;
  Object.assign(me, { rot: 0, rotAround: 'feet', jump: 0, bob: 0 });
  resetPose(me);
  if (me.alpha >= 0.5) { me.anchor = null; await fallDown(g); return; }
  if (fighterOf(D, me).hp <= 0) return;                  // beaten in there: the end takes it from here
  await wait(rand(350, 650), g);
  popBack(me, S.home && spotPoint(me, S.home) ? offOf(me, S.home, S.from, D) : null, D);
  await dizzy(g);
}

// ---------- moving about ----------
// A quick move along its surface by dx, in a little arc `lift` of its pixels high, over ms; with
// spin (1 or -1, the way it goes) it turns a somersault on the way.
async function nudge(g, dx, lift, ms, D, spin = 0) {
  const me = g.me, a = me.anchor, base = a && baseOf(a), sp = base && span(me, base, D);
  if (!sp || a.rest === 'lean') return;
  const x0 = me.x, x1 = clamp(x0 + dx, sp.lo, sp.hi);
  me.moving = true; me.anchor = null;
  if (spin) me.rotAround = 'center';
  try {
    await animate(ms, g, t => {
      const q = base.geom() || sp.g;
      me.x = lerp(x0, x1, easeOut(t)); me.y = q.y;
      me.jump = 4 * lift * me.s * t * (1 - t);
      if (spin) me.rot = spin * 2 * Math.PI * t;
    });
  } finally {
    me.jump = 0; me.moving = false;
    if (spin) { me.rot = 0; me.rotAround = 'feet'; }
  }
  const q = base.geom() || sp.g;
  arrive(me, { ledge: base, dx: me.x - q.x1, rest: 'stand' });
}

// How far it could move along its surface in direction `way`.
function roomToward(me, way, D) {
  const sp = baseUnder(me) && span(me, baseUnder(me), D);
  return !sp || me.anchor.rest === 'lean' ? 0 : Math.max(0, way > 0 ? sp.hi - me.x : me.x - sp.lo);
}

// A spot on its surface dx from where it is.
const along = (me, dx) => { const base = baseUnder(me), g = base.geom(); return { ledge: base, dx: me.x + dx - g.x1, rest: 'stand' }; };

// A spot along its surface in direction `way`, between lo and hi of its pixels away and not on
// top of anyone, or null.
function freeAlong(me, way, lo, hi, D) {
  const room = roomToward(me, way, D);
  for (let i = 0; i < 4; i++) {
    const d = Math.min(room, rand(lo, hi) * me.s);
    if (d > 6 * me.s && !crowded(me, me.x + way * d, me.y)) return along(me, way * d);
  }
  return null;
}

// Its fast way to a spot: its own (flying, teleporting, digging...) or its world's (a pearl, a
// hook, the elytra or the jetpack, now and then a portal or a wormhole; a hook only to another
// surface); null now and then, and when it has none.
const FAST = { pearl: 3, hook: 2, elytra: 1.5, jetpack: 1.5, portal: 0.6, wormhole: 0.8 };
function fastWay(me, spot) {
  const across = baseOf(spot).id !== baseUnder(me)?.id;
  const own = ownTravel(me.design).filter(w => can(me, w) && ability(w)?.travel && !ability(w).down);
  const ways = [...own.map(w => [w, 3]), ...Object.entries(FAST).filter(([w]) => can(me, w) && (across || w !== 'hook'))];
  return chance(0.85) ? weighted(ways, w => w[1])?.[0] || null : null;
}

// To a spot: another surface its own way (a route of hops, or its far ways: travel.js); along its
// own surface it runs, or takes a fast way when it's far.
async function goTo(g, spot) {
  const me = g.me, pt = spotPoint(me, spot);
  if (!pt) return;
  if (!me.anchor || baseOf(me.anchor).id !== baseOf(spot).id) { await travelTo(spot, g); return; }
  const way = Math.abs(pt.x - me.x) > 60 * env.S && fastWay(me, spot);
  if (way) { await travelTo(spot, g, way); return; }
  me.hurry = true;
  try { await travelTo(spot, g, 'walk'); } finally { me.hurry = false; }
}

// Would it stand right on top of another companion there?
const crowded = (me, x, y) => cast.some(o => o !== me && o.alpha > 0.5 && Math.abs(o.y - y) < 10 * env.S && Math.abs(o.x - x) < half(o) + half(me) + 2 * env.S);

// A spot on any surface between lo and hi from the other (from where it's going, if it's on its
// way), not on top of anyone: getting away (away), further than now and rather on another
// surface; else rather on its own.
function spotAround(me, foe, D, lo, hi, away) {
  const to = goal(foe), now = apart(me, to), cands = [];
  for (const l of D.ledges()) {
    if (l.kind !== 'edge' && l.kind !== 'floor') continue;
    const sp = span(me, l, D);
    if (!sp || sp.g.y < height(me) + 6 * env.S) continue;                 // all of it on screen
    for (let i = 0; i < 5; i++) {
      const x = rand(sp.lo, sp.hi), d = Math.hypot(x - to.x, sp.g.y - to.y);
      if (d < lo || d > hi || (away && d <= now) || crowded(me, x, sp.g.y) || !visibleOn(l, x, sp.g.y, me.s)) continue;
      cands.push({ spot: { ledge: l, dx: x - sp.g.x1, rest: 'stand' }, w: (l === baseUnder(me)) === away ? 1 : 2 });
    }
  }
  return weighted(cands, c => c.w)?.spot || null;
}

// Can it shoot at the other from where it is?
const canShoot = (A, B) => !!A.anchor && B.alpha >= 0.5 && gapBetween(A, B) > 0 && apart(A, B) <= SHOT * env.S;

// Facing the other, ready, for ms: up on its toes, bouncing a little (a flier just hovers).
async function stance(g, foe, ms) {
  const me = g.me, t0 = clockNow(), still = me.design.body.legs?.style === 'none';
  lookAt(me, foe);
  try {
    while (clockNow() - t0 < ms) {
      await tick(g);
      lookAt(me, foe);
      if (!still) me.bob = Math.floor((clockNow() - t0) / 240) % 2 ? Math.max(1, Math.round(me.s * 0.4)) : 0;
    }
  } finally { me.bob = 0; }
}

// Now and then a taunt instead: a hop with its arms up, or "come on" with one arm.
async function taunt(g, foe) {
  const me = g.me, p = me.pose;
  lookAt(me, foe);
  if (chance(0.5)) {
    p.armL = p.armR = 'up';
    await happyHop(g, 1);
  } else {
    const key = foe.x < me.x ? 'armL' : 'armR';
    for (let k = 0; k < 2; k++) { p[key] = 'up'; await wait(170, g); p[key] = 'side'; await wait(150, g); }
  }
  p.armL = p.armR = 'side';
}

// The spot, moved beside A if it would be right on it.
function offOf(me, spot, A, D) {
  const pt = spotPoint(me, spot), need = half(A) + half(me) + 2 * env.S, sp = span(me, baseOf(spot), D);
  if (!pt || !sp || spot.dx === undefined || Math.abs(pt.x - A.x) >= need || Math.abs(pt.y - A.y) > 10 * env.S) return spot;
  const side = Math.sign(pt.x - A.x) || 1, x = [side, -side].map(k => A.x + k * need).find(c => c >= sp.lo && c <= sp.hi);
  return x === undefined ? spot : { ...spot, dx: x - sp.g.x1 };
}

// Back on screen with a puff, at the spot (or on what's under it), at its own size.
function popBack(me, spot, D) {
  if (spot) arrive(me, spot);
  else { me.anchor = null; landHere(me, D); }
  snapShape(me);
  me.alpha = 1;
  fx.puff(me.x, me.y - height(me) / 2, me.s);
}

// It stands on whatever is under it, right away.
function landHere(me, D) {
  const below = surfaceBelow(me.x, me.y - 1), sp = span(me, below.ledge, D);
  arrive(me, { ledge: below.ledge, dx: (sp ? clamp(me.x, sp.lo, sp.hi) : me.x) - below.g.x1, rest: 'stand' });
}

// A little dizzy: stars around its head, eyes shut, a shake of the head.
async function dizzy(g) {
  const me = g.me, s = me.s, p = me.pose, top = me.y - height(me);
  for (let k = 0; k < 3; k++) fx.star(me.x + (k - 1) * 4 * s, top - rand(1, 3) * s, 500 + k * 150);
  p.eyes = 'closed';
  me.rotAround = 'feet';
  try { await animate(520, g, t => { me.rot = 0.1 * Math.sin(t * Math.PI * 4) * (1 - t); }); } finally { me.rot = 0; }
  p.eyes = 'center';
}

// One attack of A's on B as in a duel, outside one (the gallery shows every attack this way): B
// just stands there and takes it.
export async function attackOnce(gA, gB, { area = null, ledges = null } = {}) {
  const D = newDuel(gA, gB, { hits: [9, 9], area, ledges }), [A, B] = D.fighters;
  Object.assign(fighterOf(D, B), { calm: true, dodgy: 0 });
  await both(perform(D, A, g => strike(D, g), true).finally(() => { D.stop = true; }), fighter(D, B));
}

// ---------- the end ----------
// Beaten (no hits left): it tips over and goes up in a puff; the winner celebrates. Run out of
// time: the one with more hits left wins (or it's a draw). Called off (the other one went): it
// looks around, puzzled. Then everyone left gets on with its life.
async function finish(D) {
  const [A, B] = D.fighters, left = me => fighterOf(D, me).hp;
  const here = D.fighters.filter(me => isCurrent(D.raw.get(me)));
  if (here.length < 2) D.result = { off: true };
  else if (!left(A) || !left(B)) D.result = { ko: true, loser: left(A) ? B : A, winner: left(A) ? A : B };
  else if (left(A) === left(B)) D.result = { draw: true };
  else D.result = { winner: left(A) > left(B) ? A : B, loser: left(A) > left(B) ? B : A };
  D.over = true;
  await both(...here.map(me => ending(D.raw.get(me), D)));
}

async function ending(g, D) {
  const me = g.me, R = D.result;
  clearProps(me);
  Object.assign(me, { tint: null, rot: 0, rotAround: 'feet', jump: 0, bob: 0, sink: 0, chute: 0, moving: false });
  if (me === R.loser && R.ko) { await beaten(g, D, R.winner); return; }
  if (me.alpha < 0.5) popBack(me, null, D);
  else if (!standsOn(me, D)) { me.anchor = null; await fallDown(g); }
  standHere(me);
  resetPose(me);
  if (R.off) { mark(me, HUH); await lookAround(g); }
  else if (me === R.winner) await victory(g, D);
  else if (R.draw) { lookAt(me, foeOf(D, me)); await wait(300, g); await happyHop(g, 1); }
  else { me.pose.eyes = 'down'; pulse(me, 'squash'); await wait(900, g); me.pose.eyes = 'center'; }
}

// No hits left (or struck by an ultra): it topples, or, taken away on its last hit, it's simply
// gone. A little later it's back somewhere else on screen, a little dizzy, and not evil any more.
async function beaten(g, D, from) {
  const me = g.me;
  if (me.alpha >= 0.5) await topple(g, D, from);
  Object.assign(me, { alpha: 0, rot: 0, evil: null });
  await wait(rand(2800, 4200), g);
  popBack(me, respawnSpot(me, D), D);
  await dizzy(g);
  await lookAround(g);
}

// It flashes red, tips over like a beaten mob (down on the ground first, if it was knocked into
// the air) and goes up in a puff; or it goes down its own way ("fall" in its design's "fight":
// Pac-Man deflates, as in the arcade).
async function topple(g, D, from) {
  const me = g.me, s = me.s, side = Math.sign(me.x - from.x) || 1, pt = me.anchor && spotPoint(me, me.anchor);
  resetPose(me);
  if ((!pt || Math.hypot(pt.x - me.x, pt.y - me.y) > 2) && !standsOn(me, D)) { me.anchor = null; await fallDown(g); }
  const fall = ability(me.design.fight?.fall)?.fall;
  if (fall) { await fall(g, from); Object.assign(me, { alpha: 0, rot: 0 }); return; }
  me.pose.eyes = 'closed';
  me.tint = HURT;
  try {
    await animate(380, g, t => { me.rot = side * (Math.PI / 2) * easeOut(t); });
    await wait(320, g);
  } finally { me.tint = null; }
  const cy = me.y - height(me) / 2;
  fx.burst(me.x, cy, s, POOF, 18, 11, -25, 800);
  fx.puff(me.x, cy, s);
  Object.assign(me, { alpha: 0, rot: 0 });
}

// Where a beaten companion comes back: anywhere on screen it can stand (in the gallery, in the picture).
function respawnSpot(me, D) {
  if (D.area) {
    const sp = span(me, FLOOR, D), xs = sp ? Array.from({ length: 6 }, () => rand(sp.lo, sp.hi)) : [];
    const x = xs.find(c => !crowded(me, c, sp.g.y)) ?? xs[0];
    return sp && { ledge: FLOOR, dx: x - sp.g.x1, rest: 'stand' };
  }
  const spot = chooseSpot(me, { only: chance(0.5) ? 'edge' : 'floor' }) || chooseSpot(me, { only: 'floor' });
  return spot && { ...spot, rest: 'stand' };
}

// The winner: arms up, eyes shut, a little shower of gold and sparkles, two happy hops; then it
// waits for the other to come back and waves to it.
async function victory(g, D) {
  const me = g.me, s = me.s, p = me.pose, loser = D.result.loser;
  await wait(500, g);
  Object.assign(p, { eyes: 'closed', armL: 'up', armR: 'up' });
  const top = me.y - height(me);
  fx.burst(me.x, top - 2 * s, s, GOLD, 16, 11, 30, 750);
  for (let k = 0; k < 3; k++) fx.star(me.x + rand(-7, 7) * s, top - rand(1, 6) * s, 500 + k * 140);
  await happyHop(g, 2);
  Object.assign(p, { eyes: 'center', armL: 'side', armR: 'side' });
  if (!loser) return;
  const t0 = clockNow(), back = () => loser.alpha >= 0.5 && isCurrent(D.raw.get(loser));
  while (!back() && isCurrent(D.raw.get(loser)) && clockNow() - t0 < 6000) await wait(100, g);
  if (!back()) return;
  await wait(500, g);
  lookAt(me, loser);
  await wave(g);
  p.eyes = 'center';
}

// ---------- watching ----------
// Someone else on screen watches a duel: it looks at the fighters, starts at every hit, hops out of
// the way when they come right at it, cheers now and then, and cheers for the winner at the end.
// Struck by an ultra, it's beaten like a fighter.
export async function watch(g, D) {
  const me = g.me, p = me.pose, struck = () => !!D.ultra?.struck.has(me);
  D.watchers.add(me);
  mark(me, BANG);
  pulse(me, 'stretch');
  let hits = D.hits, cheerAt = clockNow() + rand(1500, 4500);
  try {
    while (!D.result && !struck()) {
      await tick(g);
      const [A, B] = D.fighters, mid = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
      const close = D.fighters.find(o => level(o, me) && Math.abs(o.x - me.x) < half(o) + half(me) + 4 * env.S);
      if (close && me.anchor) { await nudge(g, (Math.sign(me.x - close.x) || 1) * rand(10, 16) * me.s, 3, 300, D); continue; }
      p.eyes = mid.x < me.x - 8 * me.s ? 'left' : mid.x > me.x + 8 * me.s ? 'right' : mid.y < me.y - 20 * me.s ? 'up' : 'center';
      if (D.hits !== hits) { hits = D.hits; pulse(me, 'squash'); p.eyes = 'wide'; await wait(260, g); }
      if (clockNow() > cheerAt) {
        cheerAt = clockNow() + rand(3000, 6500);
        p.armL = p.armR = 'up';
        await happyHop(g, 1);
        p.armL = p.armR = 'side';
      }
    }
  } finally { D.watchers.delete(me); }
  if (struck()) { await beaten(g, D, D.ultra.by); return; }
  if (!D.result?.winner) return;
  await wait(400, g);
  Object.assign(p, { eyes: 'closed', armL: 'up', armR: 'up' });
  fx.heart(me.x, me.y - height(me) - 2 * me.s, me.s);
  await happyHop(g, 2);
  Object.assign(p, { eyes: 'center', armL: 'side', armR: 'side' });
}

// ---------- evil ----------
// It shivers, dark smoke curling off it, and its eyes light up (me.evil: drawn with glowing eyes,
// see evilLook in skins.js); an evil little laugh of three quick hops. It stays evil for ms.
export async function turnEvil(g, ms = rand(55000, 85000)) {
  const me = g.me, s = me.s, p = me.pose;
  standHere(me);
  setShape(me, 'normal', 'front');
  p.eyes = 'closed';
  me.rotAround = 'feet';
  try {
    await animate(800, g, t => {
      me.rot = (Math.floor(t * 30) % 2 ? 1 : -1) * 0.05 * t;
      if (chance(0.4)) smoke(me);
    });
  } finally { me.rot = 0; }
  me.evil = { until: clockNow() + ms, chased: new Set() };
  p.eyes = 'center';
  fx.burst(me.x, me.y - height(me) * 0.7, s, ['#FF2A2A', '#FF8A8A', '#8E1B2C'], 14, 9, 0, 500);
  pulse(me, 'stretch');
  await wait(250, g);
  for (let k = 0; k < 3; k++) await animate(170, g, t => { me.jump = 4 * 1.3 * s * t * (1 - t); });
  me.jump = 0;
  await wait(300, g);
}

// A wisp of dark smoke off it.
function smoke(me) {
  const s = me.s;
  fx.emit({ kind: 'square', x: me.x + rand(-1, 1) * half(me), y: me.y - rand(0.2, 1) * height(me), vx: rand(-2, 2) * s, vy: -rand(4, 9) * s,
    life: rand(400, 700), size: rand(0.5, 0.9), color: pick(EVIL_SMOKE), shrink: true, fade: 0.8 });
}

// An evil one goes after the next one (victims: the companions it could go after now): over to it,
// its own way. One that fights it has a duel with it (duelWith); the rest run (scare) while it
// stomps and glares. With nobody to go after it prowls about; when its time is up it calms down.
// area: stay inside it (the gallery's picture).
export async function hunt(g, { victims = [], duelWith, scare, area = null }) {
  const me = g.me, E = me.evil, D = { area };
  if (!E) return;
  if (clockNow() > E.until) { await calmDown(g); return; }
  const left = victims.filter(o => o !== me && !E.chased.has(o));
  if (!left.length) { await prowl(g, D); return; }
  const T = left.reduce((a, b) => (apart(me, a) < apart(me, b) ? a : b));
  E.chased.add(T);
  E.target = T;                                           // (the others leave it be: it's after this one)
  lookAt(me, T);
  mark(me, ANGER);
  await wait(400, g);
  const sp = span(me, baseUnder(T), D);
  if (sp && apart(me, T) > 30 * env.S) {
    const side = Math.sign(me.x - T.x) || 1, want = half(T) + half(me) + 10 * env.S;
    const x = [side, -side].map(k => T.x + k * want).find(c => c >= sp.lo && c <= sp.hi) ?? clamp(T.x + side * want, sp.lo, sp.hi);
    await goTo(g, { ledge: baseUnder(T), dx: x - sp.g.x1, rest: 'stand' });
  }
  if (can(T, 'fight.duel')) {
    duelWith(me, T);
    await tick(g);                   // a duel took over: this run stops here (else T was busy: it glares)
  } else scare(T, me);
  await menace(g, T);
}

// It stomps at the one running away, a puff of dust each time, and glares after it.
async function menace(g, T) {
  const me = g.me, s = me.s;
  lookAt(me, T);
  for (let k = 0; k < 2; k++) {
    await animate(200, g, t => { me.jump = 4 * 1.5 * s * t * (1 - t); });
    me.jump = 0;
    pulse(me, 'squash');
    fx.burst(me.x, me.y, s, DUST, 6, 6, 10, 320);
    await wait(160, g);
  }
  for (let k = 0; k < 10; k++) { lookAt(me, T); await wait(120, g); }
}

// Nobody to go after just now: a few steps about, glaring, smoke curling off it.
async function prowl(g, D) {
  const me = g.me, dir = chance(0.5) ? 1 : -1, room = roomToward(me, dir, D);
  if (room > 8 * me.s) await nudge(g, dir * Math.min(room, rand(8, 20) * me.s), 1, 700, D);
  for (let k = 0; k < 12; k++) { if (chance(0.3)) smoke(me); await wait(180, g); }
}

// Its time is up: it stops and shakes its head, and it's itself again (a little embarrassed).
export async function calmDown(g) {
  const me = g.me, s = me.s, p = me.pose;
  if (!me.evil) return;
  standHere(me);
  setShape(me, 'normal', 'front');
  p.eyes = 'closed';
  me.rotAround = 'feet';
  try { await animate(700, g, t => { me.rot = 0.12 * Math.sin(t * Math.PI * 6) * (1 - t); }); } finally { me.rot = 0; }
  me.evil = null;
  const top = me.y - height(me);
  fx.burst(me.x, top + 3 * s, s, ['#FFFFFF', '#E6DCFF', '#FFE38A'], 12, 8, 0, 500);
  fx.star(me.x, top - s, 500);
  p.eyes = 'down';
  await wait(800, g);
  fx.heart(me.x, top - 2 * s, s);
  p.eyes = 'center';
  await wait(300, g);
}

// One that doesn't fight runs from an evil one: three "!", and away along its surface (or off to
// somewhere else, cornered), then a look back.
export async function runFrom(g, from, area = null) {
  const me = g.me, s = me.s, away = Math.sign(me.x - from.x) || 1;
  for (let k = 0; k < 3; k++) fx.emit({ kind: 'sprite', art: BANG, x: me.x + (k - 1) * 3 * s, y: (me.bbox ? me.bbox.t : me.y - height(me)) - 4 * s, vy: -4 * s, life: 600, scale: Math.max(1, Math.round(s * 0.8)) });
  me.pose.eyes = away > 0 ? 'right' : 'left';
  pulse(me, 'stretch');
  await wait(200, g);
  const there = freeAlong(me, away, 25, 55, { area });
  if (there) {
    me.hurry = true;
    try { await travelTo(there, g, 'walk'); } finally { me.hurry = false; }
  } else {
    const spot = chooseSpot(me, { filter: l => l.id !== baseUnder(me)?.id });
    if (spot) await travelTo(spot, g);
  }
  lookAt(me, from);
  await wait(600, g);
}

// Wary of an evil one close by: it keeps an eye on it and backs off a few steps.
export async function keepAway(g, from) {
  const me = g.me, there = freeAlong(me, Math.sign(me.x - from.x) || 1, 14, 26, null);
  lookAt(me, from);
  if (there) await travelTo(there, g, 'walk');
  lookAt(me, from);
  await wait(rand(800, 1500), g);
}
